// ============================================
// FILE: hooks/useAuth.ts
// ============================================
import { useEffect, useCallback } from 'react';
import {
  login as loginService,
  logout as logoutService,
  logoutAllDevices as logoutAllDevicesService
} from '../stateManagers/services/authService';
import { useAuthStore } from '../stateManagers/store/authStore';
import { decodeAccessToken } from '../apiSetup/decodeToken';

export const useAuth = () => {
  const {
    isAuthenticated,
    user,
    accessToken,
    refreshToken,
    loading,
    setAuth,
    clearAuth,
    initAuth
  } = useAuthStore();

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const login = useCallback(async (username: string, password: string) => {
    try {
      console.log('🔐 useAuth: Starting login process...');
      const response = await loginService(username, password);

      console.log('📦 useAuth: Full response:', response.data);

      // Backend returns a flat shape: { status, message, token, refresh_token }
      const { status, token, refresh_token } = response.data;

      if (status !== 'success' || !token || !refresh_token) {
        console.error('❌ useAuth: Missing tokens in response!');
        throw new Error('Invalid response: missing authentication tokens');
      }

      // No user object comes back from login — decode it from the JWT payload
      const decoded = decodeAccessToken(token);
      if (!decoded) {
        throw new Error('Failed to decode access token');
      }
      const user = decoded.user;

      console.log('🔑 useAuth: Access token:', token);
      console.log('🔄 useAuth: Refresh token:', refresh_token);
      console.log('👤 useAuth: User (decoded from token):', user);

      console.log('💾 useAuth: Saving to localStorage...');
      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));

      const savedAccessToken = localStorage.getItem('accessToken');
      const savedRefreshToken = localStorage.getItem('refreshToken');
      console.log('✅ useAuth: Verified saved accessToken:', savedAccessToken ? 'EXISTS' : 'MISSING');
      console.log('✅ useAuth: Verified saved refreshToken:', savedRefreshToken ? 'EXISTS' : 'MISSING');

      console.log('🔄 useAuth: Updating auth store...');
      setAuth({
        isAuthenticated: true,
        user,
        accessToken: token,
        refreshToken: refresh_token,
        loading: false
      });

      const finalCheck = localStorage.getItem('accessToken');
      console.log('🎯 useAuth: Final localStorage check:', finalCheck ? 'TOKEN EXISTS' : 'TOKEN MISSING');

      console.log('✅ useAuth: Login complete!');
      return response;
    } catch (error: any) {
      console.error('❌ useAuth: Login error:', error);
      console.error('❌ useAuth: Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      throw new Error(errorMessage);
    }
  }, [setAuth]);

  const logout = useCallback(async () => {
    try {
      console.log('👋 useAuth: Starting logout...');
      if (accessToken && refreshToken) {
        await logoutService(accessToken, refreshToken);
      }
    } catch (error) {
      console.error('❌ useAuth: Logout error:', error);
    } finally {
      console.log('🧹 useAuth: Clearing auth data...');
      clearAuth();
    }
  }, [accessToken, refreshToken, clearAuth]);

  const logoutAll = useCallback(async () => {
    try {
      console.log('👋 useAuth: Logging out all devices...');
      if (accessToken) {
        await logoutAllDevicesService(accessToken);
      }
    } catch (error) {
      console.error('❌ useAuth: Logout all error:', error);
    } finally {
      console.log('🧹 useAuth: Clearing auth data...');
      clearAuth();
    }
  }, [accessToken, clearAuth]);

  return {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    logoutAll
  };
};